import React, { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';

// Pre-computed snowflake styles (generated once at module load)
const SNOWFLAKE_STYLES = [
  { left: '5%', animationDelay: '0.2s', animationDuration: '4.5s', size: 10 },
  { left: '15%', animationDelay: '1.5s', animationDuration: '5.2s', size: 12 },
  { left: '25%', animationDelay: '0.8s', animationDuration: '4.8s', size: 8 },
  { left: '35%', animationDelay: '2.1s', animationDuration: '5.5s', size: 14 },
  { left: '45%', animationDelay: '0.5s', animationDuration: '4.2s', size: 9 },
  { left: '55%', animationDelay: '1.8s', animationDuration: '5.8s', size: 11 },
  { left: '65%', animationDelay: '0.3s', animationDuration: '4.6s', size: 13 },
  { left: '75%', animationDelay: '2.5s', animationDuration: '5.0s', size: 10 },
  { left: '85%', animationDelay: '1.2s', animationDuration: '4.4s', size: 15 },
  { left: '95%', animationDelay: '0.7s', animationDuration: '5.3s', size: 9 },
  { left: '10%', animationDelay: '3.0s', animationDuration: '6.0s', size: 11 },
  { left: '20%', animationDelay: '2.3s', animationDuration: '4.9s', size: 12 },
  { left: '30%', animationDelay: '1.0s', animationDuration: '5.4s', size: 8 },
  { left: '40%', animationDelay: '3.5s', animationDuration: '4.3s', size: 14 },
  { left: '50%', animationDelay: '0.9s', animationDuration: '5.7s', size: 10 },
  { left: '60%', animationDelay: '2.8s', animationDuration: '4.7s', size: 13 },
  { left: '70%', animationDelay: '1.6s', animationDuration: '5.1s', size: 9 },
  { left: '80%', animationDelay: '0.4s', animationDuration: '6.2s', size: 11 },
  { left: '90%', animationDelay: '2.0s', animationDuration: '4.1s', size: 15 },
  { left: '3%', animationDelay: '1.3s', animationDuration: '5.6s', size: 10 },
];

/**
 * Holiday Popup Component
 * Shows a festive greeting on first login
 * Auto-disables after Jan 2, 2026
 */
const HolidayPopup = ({ 
  isAuthenticated, 
  hasSeenHolidayPopup, 
  onClose, 
  enabled = true 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Check if holiday popup should be shown
  const shouldShow = (() => {
    const cutoffDate = new Date('2026-01-02T00:00:00');
    const now = new Date();
    if (now >= cutoffDate || !enabled) return false;
    if (!isAuthenticated || hasSeenHolidayPopup) return false;
    return true;
  })();

  useEffect(() => {
    if (!shouldShow) return;
    
    // Small delay for better UX after login
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [shouldShow]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      onClose?.();
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop with blur */}
      <div 
        className={`fixed inset-0 z-[10000] transition-opacity duration-300 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
        onClick={handleClose}
      />

      {/* Modal */}
      <div 
        className={`fixed inset-0 z-[10001] flex items-center justify-center p-4 transition-all duration-300 ${
          isClosing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        }`}
      >
        <div 
          className="relative max-w-lg w-full rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.2)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Inner snowflakes animation */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {SNOWFLAKE_STYLES.map((style, i) => (
              <div
                key={`snowflake-${style.left}-${style.animationDelay}-${i}`}
                className="absolute animate-fall"
                style={{
                  left: style.left,
                  top: `-20px`,
                  animationDelay: style.animationDelay,
                  animationDuration: style.animationDuration,
                }}
              >
                <Sparkles 
                  className="text-white/30" 
                  size={style.size} 
                />
              </div>
            ))}
          </div>

          {/* Decorative top border */}
          <div 
            className="h-1.5"
            style={{
              background: 'linear-gradient(90deg, #c41e3a 0%, #ffd700 33%, #228b22 66%, #c41e3a 100%)',
            }}
          />

          {/* Content */}
          <div className="relative p-8 text-center">
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-white/70" />
            </button>

            {/* Holiday icon */}
            <div className="mb-6">
              <div 
                className="inline-flex items-center justify-center w-20 h-20 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(196, 30, 58, 0.3) 0%, rgba(255, 215, 0, 0.2) 100%)',
                  border: '2px solid rgba(255, 215, 0, 0.4)',
                }}
              >
                <span className="text-4xl">🎄</span>
              </div>
            </div>

            {/* Greeting text */}
            <h2 
              className="text-2xl md:text-3xl font-bold mb-4"
              style={{
                background: 'linear-gradient(135deg, #ffd700 0%, #fff 50%, #ffd700 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Happy Holidays from SmartPDF Tools!
            </h2>

            <p className="text-white/80 text-base md:text-lg leading-relaxed mb-2">
              Wishing you joy, peace, and productivity this season.
            </p>
            
            <p className="text-white/60 text-sm mb-8">
              Thank you for being one of our early users! ❄️
            </p>

            {/* Decorative divider */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-white/30" />
              <Sparkles className="w-4 h-4 text-yellow-400/70" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-white/30" />
            </div>

            {/* Continue button */}
            <button
              onClick={handleClose}
              className="px-8 py-3 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-105 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, #c41e3a 0%, #8b0000 100%)',
                boxShadow: '0 4px 15px rgba(196, 30, 58, 0.4)',
              }}
            >
              ✨ Continue
            </button>

            {/* Subtle footer text */}
            <p className="mt-6 text-xs text-white/40">
              Seasons Greetings 2025 🎁
            </p>
          </div>

          {/* Decorative bottom border */}
          <div 
            className="h-1"
            style={{
              background: 'linear-gradient(90deg, #228b22 0%, #ffd700 33%, #c41e3a 66%, #228b22 100%)',
            }}
          />
        </div>
      </div>

      {/* CSS for snowflake animation inside modal */}
      <style>{`
        @keyframes fall {
          0% {
            transform: translateY(-20px) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(400px) rotate(360deg);
            opacity: 0;
          }
        }
        .animate-fall {
          animation: fall linear infinite;
        }
      `}</style>
    </>
  );
};

export default HolidayPopup;
